import React, { useState, useRef } from 'react';
import { db, storage } from '../firebase';
import {
  doc, collection, setDoc, updateDoc, serverTimestamp,
} from 'firebase/firestore';
import { ref as storageRef, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { useAuth } from '../AuthContext';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fileIcon(name = '') {
  const ext = name.split('.').pop().toLowerCase();
  if (['doc', 'docx'].includes(ext)) return '📝';
  if (['xls', 'xlsx'].includes(ext)) return '📊';
  if (['ppt', 'pptx'].includes(ext)) return '📑';
  if (ext === 'pdf') return '📄';
  if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) return '🖼️';
  return '📎';
}

function formatBytes(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// ── TaskCreator ───────────────────────────────────────────────────────────────

export default function TaskCreator({
  curriculumId,
  subjectName,
  chapterName,
  approvedStudents = [],
  existingTask = null,
  onSave,
  onCancel,
}) {
  const { currentUser, userProfile } = useAuth();

  const [title, setTitle] = useState(existingTask?.title || '');
  const [description, setDescription] = useState(existingTask?.description || '');
  const [dueDate, setDueDate] = useState(existingTask?.dueDate || '');
  const [selectedStudentIds, setSelectedStudentIds] = useState(
    existingTask?.assignedStudentIds || []
  );

  const [existingAttachments, setExistingAttachments] = useState(
    existingTask?.attachments || []
  );
  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef();

  const toggleStudent = (id) =>
    setSelectedStudentIds((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );

  const selectAll = () =>
    setSelectedStudentIds(approvedStudents.map((s) => s.studentId));

  const clearAll = () => setSelectedStudentIds([]);

  const handleFilePick = (e) => {
    const files = Array.from(e.target.files);
    const tooBig = files.filter((f) => f.size > 25 * 1024 * 1024);
    if (tooBig.length) {
      setError(`File exceeds 25 MB limit: ${tooBig.map((f) => f.name).join(', ')}`);
      return;
    }
    setPendingFiles((prev) => [...prev, ...files]);
    setError('');
    e.target.value = '';
  };

  const removeExistingAttachment = (idx) =>
    setExistingAttachments((prev) => prev.filter((_, i) => i !== idx));

  const removePendingFile = (idx) =>
    setPendingFiles((prev) => prev.filter((_, i) => i !== idx));

  const uploadFiles = async (taskId, files) => {
    const results = [];
    for (const file of files) {
      const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
      const path = `task-attachments/${taskId}/${Date.now()}_${safeName}`;
      const sRef = storageRef(storage, path);
      await new Promise((resolve, reject) => {
        const uploadTask = uploadBytesResumable(sRef, file);
        uploadTask.on(
          'state_changed',
          (snap) => {
            const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
            setUploadProgress((prev) => ({ ...prev, [file.name]: pct }));
          },
          (err) => {
            console.error('Upload error for', file.name, err);
            reject(err);
          },
          async () => {
            try {
              const url = await getDownloadURL(uploadTask.snapshot.ref);
              results.push({ name: file.name, url, path, type: file.type, size: file.size });
              resolve();
            } catch (err) {
              reject(err);
            }
          }
        );
      });
    }
    return results;
  };

  const friendlyError = (err) => {
    const code = err?.code || '';
    if (code === 'permission-denied' || code === 'PERMISSION_DENIED')
      return 'Permission denied — Firestore rules may not be published yet. Please publish firestore.rules in Firebase Console.';
    if (code.startsWith('storage/unauthorized'))
      return 'Storage permission denied — Storage rules may not be published yet. Please publish storage.rules in Firebase Console, and make sure Firebase Storage is enabled.';
    if (code.startsWith('storage/'))
      return `Storage error (${code}): Make sure Firebase Storage is enabled in your Firebase Console project.`;
    if (code === 'unavailable')
      return 'Network unavailable — check your internet connection and try again.';
    return err?.message || 'Save failed. Check the browser console for details.';
  };

  const handleSave = async () => {
    setError('');
    if (!title.trim()) { setError('Please enter a task title.'); return; }
    if (selectedStudentIds.length === 0) {
      setError('Assign this task to at least one student.');
      return;
    }

    setSaving(true);
    try {
      const isNew = !existingTask;
      const taskRef = isNew
        ? doc(collection(db, 'tasks'))
        : doc(db, 'tasks', existingTask.id);

      const newAttachments = pendingFiles.length
        ? await uploadFiles(taskRef.id, pendingFiles)
        : [];

      const assignedStudentNames = {};
      selectedStudentIds.forEach((sid) => {
        const found = approvedStudents.find((a) => a.studentId === sid);
        if (found) assignedStudentNames[sid] = found.studentName;
      });

      const payload = {
        teacherId: currentUser.uid,
        teacherName: userProfile?.name || '',
        curriculumId,
        subjectName,
        chapterName,
        title: title.trim(),
        description: description.trim(),
        dueDate: dueDate || null,
        assignedStudentIds: selectedStudentIds,
        assignedStudentNames,
        attachments: [...existingAttachments, ...newAttachments],
        updatedAt: serverTimestamp(),
      };

      if (isNew) {
        payload.createdAt = serverTimestamp();
        await setDoc(taskRef, payload);
      } else {
        await updateDoc(taskRef, payload);
      }

      onSave?.();
    } catch (err) {
      console.error('TaskCreator save error:', err);
      setError(friendlyError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="tc-overlay animate-fade" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.3)',
      backdropFilter: 'blur(8px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px'
    }}>
      <div className="tc-panel animate-in" style={{
        background: 'rgba(255, 255, 255, 0.85)',
        backdropFilter: 'blur(16px)',
        borderRadius: '24px',
        width: '100%',
        maxWidth: '540px',
        maxHeight: '90vh',
        overflow: 'hidden',
        boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid rgba(255,255,255,0.4)'
      }}>
        <div className="tc-header" style={{
          padding: '24px 32px',
          borderBottom: '1px solid rgba(0,0,0,0.05)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: 'var(--text-primary)' }}>
              {existingTask ? '✏️ Edit Task' : '📋 Create New Task'}
            </h3>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: 'var(--text-secondary)' }}>
              {subjectName} › {chapterName}
            </p>
          </div>
          <button 
            className="icon-btn" 
            onClick={onCancel} 
            disabled={saving}
            style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(0,0,0,0.05)', border: 'none', cursor: 'pointer' }}
          >✕</button>
        </div>

        <div className="tc-body" style={{ padding: '32px', overflowY: 'auto', flex: 1 }}>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: '700', marginBottom: '8px', color: 'var(--text-primary)' }}>Task Title *</label>
          <input
            className="form-input"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g. Worksheet #1"
            disabled={saving}
            style={{ width: '100%', padding: '12px 16px', borderRadius: '12px', border: '1px solid rgba(0,0,0,0.1)', background: '#fff', fontSize: '15px' }}
          />

          <label style={{ display: 'block', fontSize: '14px', fontWeight: '700', marginBottom: '8px', marginTop: '20px', color: 'var(--text-primary)' }}>
            Instructions <span style={{ fontWeight: '400', color: 'var(--text-secondary)', fontSize: '12px' }}>(optional)</span>
          </label>
          <textarea
            className="form-input form-textarea"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Provide details for the students..."
            rows={3}
            disabled={saving}
            style={{ width: '100%', padding: '12px 16px', borderRadius: '12px', border: '1px solid rgba(0,0,0,0.1)', background: '#fff', fontSize: '15px', resize: 'vertical' }}
          />

          <label style={{ display: 'block', fontSize: '14px', fontWeight: '700', marginBottom: '8px', marginTop: '20px', color: 'var(--text-primary)' }}>
            Due Date <span style={{ fontWeight: '400', color: 'var(--text-secondary)', fontSize: '12px' }}>(optional)</span>
          </label>
          <input
            type="date"
            className="form-input"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            disabled={saving}
            style={{ width: '100%', padding: '12px 16px', borderRadius: '12px', border: '1px solid rgba(0,0,0,0.1)', background: '#fff', fontSize: '15px' }}
          />

          {/* ── Assign Students header ── */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '20px 0 8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <label style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-primary)', margin: 0 }}>
                Assign to Students *
              </label>
              {approvedStudents.length > 0 && (
                <span style={{ fontSize: '12px', color: 'var(--primary)', fontWeight: '600', background: 'var(--primary-light)', padding: '2px 8px', borderRadius: '20px' }}>
                  {selectedStudentIds.length}/{approvedStudents.length} selected
                </span>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <button
                type="button"
                onClick={selectAll}
                disabled={saving}
                style={{ background: 'none', border: 'none', color: 'var(--primary)', fontSize: '13px', fontWeight: '600', cursor: 'pointer', padding: '4px 8px', borderRadius: '6px' }}
              >Select All</button>
              <span style={{ color: '#ccc' }}>·</span>
              <button
                type="button"
                onClick={clearAll}
                disabled={saving}
                style={{ background: 'none', border: 'none', color: '#888', fontSize: '13px', fontWeight: '600', cursor: 'pointer', padding: '4px 8px', borderRadius: '6px' }}
              >Clear</button>
            </div>
          </div>

          {/* ── Student list ── */}
          <div style={{
            border: '1.5px solid #ede9f0',
            borderRadius: '12px',
            background: '#fff',
            flexShrink: 0,
          }}>
            {approvedStudents.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#888', fontSize: '13px' }}>
                No approved students yet.
              </div>
            ) : (
              approvedStudents.map((s, idx) => {
                const checked = selectedStudentIds.includes(s.studentId);
                return (
                  <label
                    key={s.studentId}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px 16px',
                      cursor: 'pointer',
                      background: checked ? '#fff5f8' : '#fff',
                      borderBottom: idx < approvedStudents.length - 1 ? '1px solid #f0eef5' : 'none',
                      transition: 'background 0.12s',
                      position: 'relative',
                    }}
                  >
                    {/* Avatar */}
                    <div style={{
                      width: '38px',
                      height: '38px',
                      borderRadius: '50%',
                      background: checked ? 'var(--primary)' : '#FFE4EC',
                      color: checked ? '#fff' : 'var(--primary)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: '800',
                      fontSize: '15px',
                      flexShrink: 0,
                    }}>
                      {s.studentName?.charAt(0)?.toUpperCase() || '?'}
                    </div>

                    {/* Name + email */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '14px', fontWeight: '600', color: '#2D2D2D', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {s.studentName}
                      </div>
                      {s.studentEmail && (
                        <div style={{ fontSize: '12px', color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {s.studentEmail}
                        </div>
                      )}
                    </div>

                    {/* Custom checkbox */}
                    <div style={{
                      width: '22px',
                      height: '22px',
                      borderRadius: '6px',
                      border: checked ? 'none' : '2px solid #ddd',
                      background: checked ? 'var(--primary)' : '#fff',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                      transition: 'all 0.12s',
                    }}>
                      {checked && (
                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                          <path d="M2 6L5 9L10 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      )}
                    </div>

                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleStudent(s.studentId)}
                      disabled={saving}
                      style={{ position: 'absolute', opacity: 0, width: 0, height: 0 }}
                    />
                  </label>
                );
              })
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '24px', marginBottom: '12px' }}>
            <label style={{ fontSize: '14px', fontWeight: '700', color: 'var(--text-primary)' }}>Attachments</label>
            <button 
              className="btn-link" 
              onClick={() => fileInputRef.current?.click()} 
              disabled={saving}
              style={{ fontSize: '13px', background: 'var(--primary-light)', color: 'var(--primary)', padding: '6px 12px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontWeight: '600' }}
            >
              📎 Add Files
            </button>
          </div>

          <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={handleFilePick} accept=".doc,.docx,.xls,.xlsx,.ppt,.pptx,.pdf,.png,.jpg,.jpeg,.txt,.csv,.zip" />

          <div className="attach-list" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {[...existingAttachments, ...pendingFiles].length === 0 && (
              <p style={{ textAlign: 'center', fontSize: '12px', color: 'var(--text-secondary)', margin: 0, padding: '10px', border: '2px dashed rgba(0,0,0,0.05)', borderRadius: '12px' }}>No files attached.</p>
            )}
            {existingAttachments.map((att, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '10px 14px', background: '#fff', borderRadius: '12px', border: '1px solid rgba(0,0,0,0.05)' }}>
                <span style={{ marginRight: '10px' }}>{fileIcon(att.name)}</span>
                <span style={{ flex: 1, fontSize: '13px', fontWeight: '500' }}>{att.name}</span>
                <button 
                  style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', padding: '4px' }}
                  onClick={() => removeExistingAttachment(i)} 
                  disabled={saving}
                >✕</button>
              </div>
            ))}

            {pendingFiles.map((f, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '10px 14px', background: '#fff', borderRadius: '12px', border: '1px solid var(--primary-light)' }}>
                <span style={{ marginRight: '10px' }}>{fileIcon(f.name)}</span>
                <div style={{ flex: 1 }}>
                  <span style={{ fontSize: '13px', fontWeight: '500' }}>{f.name} <em style={{ fontSize: '11px', fontStyle: 'normal', opacity: 0.5 }}>{formatBytes(f.size)}</em></span>
                  {uploadProgress[f.name] !== undefined && (
                    <div style={{ height: '4px', background: 'rgba(0,0,0,0.05)', borderRadius: '2px', marginTop: '4px' }}>
                      <div style={{ height: '100%', background: 'var(--primary)', width: `${uploadProgress[f.name]}%`, borderRadius: '2px', transition: 'width 0.3s' }} />
                    </div>
                  )}
                </div>
                {!uploadProgress[f.name] && (
                  <button 
                    style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', padding: '4px' }}
                    onClick={() => removePendingFile(i)} 
                    disabled={saving}
                  >✕</button>
                )}
              </div>
            ))}
          </div>

          {error && <p style={{ marginTop: '16px', padding: '10px', background: '#fff3f3', border: '1px solid #ffcccc', borderRadius: '8px', color: '#d32f2f', fontSize: '13px' }}>{error}</p>}
        </div>

        <div className="tc-footer" style={{ padding: '24px 32px', borderTop: '1px solid rgba(0,0,0,0.05)', display: 'flex', gap: '12px' }}>
          <button className="btn-outline" style={{ flex: 1, padding: '12px' }} onClick={onCancel} disabled={saving}>Cancel</button>
          <button 
            className="btn-primary" 
            style={{ flex: 2, padding: '12px' }} 
            onClick={handleSave} 
            disabled={saving}
          >
            {saving ? 'Processing...' : (existingTask ? 'Update Task' : 'Post Task')}
          </button>
        </div>
      </div>
    </div>
  );
}
